import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const List = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [departments, setDepartments] = useState([]);
  const [careers, setCareers] = useState([]);
  const [highlightedRow, setHighlightedRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null); // State for success message

  const navigate = useNavigate();

  const fetchCareerData = useCallback(async (filters = {}) => {
    setLoading(true);
    const formData = new URLSearchParams();
    formData.append('name', filters.name || '');
    formData.append('department', filters.department || '');
    formData.append('status', filters.status || '');

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_KEY}career/careerList`, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      if (response.data.success) {
        setCareers(response.data.data || []);
      } else {
        setError('Error fetching career list: ' + response.data.message);
      }
    } catch (error) {
      setError('Error fetching careers: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_KEY}career/departments`);
        if (response.data.success && Array.isArray(response.data.data[0])) {
          setDepartments(response.data.data[0]);
        } else {
          setDepartments([]);
        }
      } catch (error) {
        setError('Error fetching departments: ' + error.message);
        setDepartments([]);
      }
    };

    fetchDepartments();
    fetchCareerData();
  }, [fetchCareerData]);

  const applyFilters = () => {
    const filters = {
      name: searchTerm,
      department: selectedDepartment,
      status: selectedStatus,
    };
    fetchCareerData(filters);
  };

  const handleEdit = (careerId) => {
    navigate(`/career-deep-dive/${careerId}`);
  };

  const toggleDropdown = (careerId) => {
    setHighlightedRow((prev) => (prev === careerId ? null : careerId));
  };

  // Handle career deactivation
  const handleDeactivate = async (careerId) => {
    try {
      const formData = new URLSearchParams();
      formData.append('id', careerId);
      
      const response = await axios.post(`${process.env.REACT_APP_API_KEY}career/deactivatCareer`, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.data.success) {
        setSuccessMessage(response.data.message); // Show success message
        fetchCareerData(); // Refresh career list
      } else {
        setError('Error deactivating career: ' + response.data.message);
      }
    } catch (error) {
      setError('Error deactivating career: ' + error.message);
    }
  };

  return (
    <>
      <div className="content container-fluid">
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col">
              <h3 className="page-title mb-0">Career List</h3>
            </div>
            <div className="col-auto">
              <button className="btn btn-green px-3" onClick={applyFilters} disabled={loading}>
                {loading ? 'Loading...' : 'Apply Filters'}
              </button>
            </div>
            <div className="col-auto">
              <button className="btn btn-primary px-3" onClick={() => navigate('/career-deep-dive')}>
                Go to Career Deep Dive
              </button>
            </div>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
        {successMessage && <div className="alert alert-success">{successMessage}</div>} {/* Success Message */}

        <div className="row g-4">
          <div className="col-sm-12">
            <div className="card card-table">
              <div className="card-body p-4">
                <div className="row g-4 mb-4">
                  <div className="col-sm-6 col-md-4">
                    <input
                      className="form-control form-search"
                      placeholder="Search by name"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="col-sm-6 col-md-4">
                    <select
                      className="form-select"
                      name="status"
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                    >
                      <option value="">Select Status</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="col-sm-6 col-md-4">
                    <select
                      className="form-select"
                      name="department"
                      value={selectedDepartment}
                      onChange={(e) => setSelectedDepartment(e.target.value)}
                    >
                      <option value="">Select Department</option>
                      {Array.isArray(departments) && departments.length > 0 ? (
                        departments.map((dept) => (
                          <option key={dept.id} value={dept.id}>
                            {dept.name}
                          </option>
                        ))
                      ) : (
                        <option disabled>No Departments Available</option>
                      )}
                    </select>
                  </div>
                </div>

                {loading ? (
                  <div>Loading careers...</div>
                ) : (
                  <div className="table-responsive table-report">
                    <table className="table align-middle">
                      <thead>
                        <tr>
                          <th scope="col">ID</th>
                          <th scope="col">Name</th>
                          <th scope="col">Department</th>
                          <th scope="col">Status</th>
                          <th scope="col" className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray(careers) && careers.length > 0 ? (
                          careers.map((career) => (
                            <tr key={career.id}>
                              <td>{career.id}</td>
                              <td>{career.name}</td>
                              <td>{career.department_names}</td>
                              <td>{career.status}</td>
                              <td className="text-center">
                                <div className="mailSetting">
                                  <div className="setting">
                                    <a
                                      className="icon-setting font-20 text-decoration-none"
                                      onClick={() => toggleDropdown(career.id)}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <i className="fa-solid fa-gear"></i>
                                    </a>
                                  </div>
                                  {highlightedRow === career.id && (
                                    <div className="setting-dropdown open">
                                      <ul>
                                        <li>
                                          <button
                                            className="btn btn-link text-decoration-none"
                                            onClick={() => handleEdit(career.id)}
                                          >
                                            Edit
                                          </button>
                                        </li>
                                        <li>
                                          <button
                                            className="btn btn-link text-decoration-none"
                                            onClick={() => handleDeactivate(career.id)}
                                          >
                                            Delete
                                          </button>
                                        </li>
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center">No careers found</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default List;
